import { Controller } from "@hotwired/stimulus";

// Inline card editing with Stripe Elements.
// The card number is entered in a Stripe-hosted iframe — it never reaches our
// server. We confirm a SetupIntent (client_secret passed in from the server),
// then POST only the resulting payment_method id so the backend can set it as
// the default card on the customer + subscription.
//
// Where a "defaultToggle" checkbox is rendered - the top-up modal - the user can
// decline that promotion and have the card used for that purchase only. Without
// the checkbox the answer stays yes, which is what every other card form means.
export default class extends Controller {
  static targets = ["card", "error", "submit", "spinner", "label", "defaultToggle"];
  static values = {
    publishableKey: String,
    clientSecret: String,
    updateUrl: String,
    // Where to go once the card is saved. Left blank, the controller stays put
    // and fires `stripe-card:saved` instead - the plan confirmation mounts this
    // form inside itself and must not lose the plan the user was about to buy.
    redirectUrl: { type: String, default: "/billing#billing" },
  };

  connect() {
    if (typeof Stripe === "undefined") {
      this.showError("Payment library failed to load. Please refresh and try again.");
      return;
    }

    this.stripe = Stripe(this.publishableKeyValue);
    const elements = this.stripe.elements();

    this.card = elements.create("card", {
      hidePostalCode: true,
      style: {
        base: {
          color: "#111827",
          fontFamily: '"Chivo", ui-sans-serif, system-ui, sans-serif',
          fontSize: "14px",
          "::placeholder": { color: "#9ca3af" },
        },
        invalid: { color: "#dc2626", iconColor: "#dc2626" },
      },
    });

    this.card.mount(this.cardTarget);

    // Live validation feedback from the iframe.
    this.card.on("change", (event) => {
      this.showError(event.error ? event.error.message : "");
    });
  }

  disconnect() {
    if (this.card) this.card.destroy();
  }

  async submit(event) {
    event.preventDefault();
    if (!this.stripe || !this.card) return;

    this.setLoading(true);
    this.showError("");

    const { setupIntent, error } = await this.stripe.confirmCardSetup(
      this.clientSecretValue,
      { payment_method: { card: this.card } }
    );

    if (error) {
      this.showError(error.message);
      this.setLoading(false);
      return;
    }

    // Card confirmed & attached — tell the backend to make it the default.
    try {
      const response = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          Accept: "application/json",
        },
        body: JSON.stringify({
          payment_method_id: setupIntent.payment_method,
          set_as_default: this.hasDefaultToggleTarget ? this.defaultToggleTarget.checked : true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (this.redirectUrlValue) {
          window.location.href = this.redirectUrlValue;
        } else {
          this.dispatch("saved", { detail: { paymentMethodId: setupIntent.payment_method } });
          this.setLoading(false);
        }
      } else {
        this.showError(data.error || "Something went wrong. Please try again.");
        this.setLoading(false);
      }
    } catch (e) {
      this.showError("Network error. Please try again.");
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = isLoading;
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !isLoading);
    if (this.hasLabelTarget) this.labelTarget.textContent = isLoading ? "Saving…" : "Save card";
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message || "";
    this.errorTarget.classList.toggle("hidden", !message);
  }
}
